import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BikeCard } from "@/components/BikeCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = [
  { v: "all", l: "All EVs" },
  { v: "electric-bicycle", l: "E-Bicycle" },
  { v: "low-speed-scooter", l: "Low Speed EV Scooters" },
  { v: "high-speed-scooter", l: "High Speed EV Scooters" },
  { v: "electric-motorcycle", l: "Electric Motorcycles" },
];

const Bikes = () => {
  const [params, setParams] = useSearchParams();
  const [bikes, setBikes] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const city = params.get("city") || "all";
  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "price_asc";
  const minRange = parseInt(params.get("min_range") || "0");

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/bikes", {
      params: {
        city: city === "all" ? undefined : city,
        category: category === "all" ? undefined : category,
        search: search || undefined,
        sort,
        min_range: minRange > 0 ? minRange : undefined,
      },
    }).then((r) => {
      // Deduplicate by model (brand + name) — the seed stores one row per city
      // but the catalogue should show one card per unique model.
      const seen = new Set();
      const dedup = [];
      for (const b of r.data) {
        const key = `${b.brand}::${b.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        dedup.push(b);
      }
      setBikes(dedup);
    }).finally(() => setLoading(false));
  }, [city, category, search, sort, minRange]);

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "all") p.delete(k); else p.set(k, v);
    setParams(p);
  };

  const cityName = cities.find((c) => c.slug === city)?.name || "All cities";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
          <p className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold font-mono-num">— RENT EV / {cityName.toUpperCase()}</p>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Rent an Electric Vehicle</h1>
          <p className="text-gray-600 mt-2">{bikes.length} EVs ready to ride{city !== "all" ? ` in ${cityName}` : ""}. Zero emissions, all convenience.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters sidebar */}
        <aside className="space-y-6 lg:sticky lg:top-24 self-start" data-testid="bikes-filter-sidebar">
          <div className="card-premium p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><SlidersHorizontal size={16} /> Filters</h3>
              <button onClick={() => setParams(new URLSearchParams())} data-testid="reset-filters-btn" className="text-xs text-gray-500 hover:text-ink flex items-center gap-1"><X size={12} />Reset</button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Search</label>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <Input value={search} onChange={(e) => set("search", e.target.value)} placeholder="Search EVs only…" data-testid="filter-search-input" className="pl-9 rounded-xl" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">City</label>
                <Select value={city} onValueChange={(v) => set("city", v)}>
                  <SelectTrigger data-testid="filter-city-select" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cities</SelectItem>
                    {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Category</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.v}
                      onClick={() => set("category", c.v)}
                      data-testid={`filter-cat-${c.v}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        category === c.v ? "bg-ink text-white border-ink" : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Min Range</label>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-mono-num font-bold">{minRange > 0 ? `${minRange}+ km` : "Any"}</span>
                </div>
                <Slider
                  min={0}
                  max={300}
                  step={10}
                  value={[minRange]}
                  onValueChange={(v) => set("min_range", v[0] === 0 ? "" : v[0].toString())}
                  data-testid="filter-range-slider"
                  className="mt-4"
                />
              </div>
            </div>
          </div>
        </aside>

        {/* Bikes grid */}
        <main>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-600 font-mono-num">{bikes.length} RESULTS</p>
            <Select value={sort} onValueChange={(v) => set("sort", v)}>
              <SelectTrigger data-testid="sort-select" className="w-44 rounded-full bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="rating">Top rated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-premium p-0 overflow-hidden">
                  <div className="aspect-[4/3] bg-gray-100 shimmer" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-gray-100 rounded w-1/3 shimmer" />
                    <div className="h-5 bg-gray-100 rounded w-2/3 shimmer" />
                    <div className="h-8 bg-gray-100 rounded w-1/2 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : bikes.length === 0 ? (
            <div className="card-premium p-16 text-center" data-testid="no-bikes-state">
              <p className="font-display font-bold text-2xl">No bikes match your filters</p>
              <p className="text-gray-600 mt-2">Try widening your search or resetting filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="bikes-grid">
              {bikes.map((b, i) => <BikeCard key={b.id} bike={b} index={i} />)}
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default Bikes;
